import {throwError as observableThrowError,  Observable, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { tap, catchError, debounceTime, switchMap } from 'rxjs/operators';
import {JsonService} from '../../../services/core/json.service';
import {ProposalApiResources} from './proposal.constants';

@Injectable({
  providedIn: 'root'
})


export class ProposalDataService {
  private baseURL:any;

  constructor(
    private http: HttpClient,
    private jsonService: JsonService,
  ) {
    this.baseURL = ProposalApiResources; 
  }

  list(model: any): Observable<any> {
    const endPoint = `${this.baseURL.list}`;
    return this.http.post<any>(endPoint, model)
                    .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  search(model: any): Observable<any> {
    const endPoint = `${this.baseURL.search}`;
    return this.http.post<any>(endPoint, model)
                    .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  create(model: any): Observable<any> {
    const endPoint = `${this.baseURL.create}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  detail(id: any, params?: any): Observable<any> {
    const endPoint = `${this.baseURL.detail}/${id}`;
    return this.http.get<any>(endPoint, { params })
      .pipe(
        tap(data => this.jsonService.doStringify(data)),
        catchError(this.errorHandlerMessage)
      );
  }
  update(id:any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.update}/${id}`;
    return this.http.patch<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  delete(id:any): Observable<any> {
    const endPoint = `${this.baseURL.delete}/${id}`;
    return this.http.delete<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  // Bulk delete: DELETE proposal/delete/multiple with { proposal_ids: [...] }
  deleteMultiple(proposalIds: any[]): Observable<any> {
    const endPoint = `${this.baseURL.deleteMultiple}`;
    return this.http.request<any>('delete', endPoint, { body: { proposal_ids: proposalIds } })
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  // Pre-check which of the selected proposals can be deleted (and why not, for the rest)
  deleteAvailability(proposalIds: any[]): Observable<any> {
    const endPoint = `${this.baseURL.deleteAvailability}`;
    return this.http.post<any>(endPoint, { proposal_ids: proposalIds })
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }
  searchItems(params: any): Observable<any> {
    const endPoint = `${this.baseURL.searchItems}`;
    return this.http.post<any>(endPoint, params)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  updateDisplayOptions(id:any, model: any): Observable<any> {
    model.loadingService = false;
    const endPoint = `${this.baseURL.updateDisplayOptions}/${id}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }
  cloneScope(id:any, model: any): Observable<any> {
    model.loadingService = false;
    const endPoint = `${this.baseURL.cloneScope}/${id}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }
  getRevisionBidders(id:any, model: any = {}): Observable<any> {
    const endPoint = `${this.baseURL.getRevisionBidders}/${id}`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }
  getCalculations(id:any): Observable<any> {
    const endPoint = `${this.baseURL.calculations}/${id}`;
    return this.http.get<any>(endPoint, {params: {scope: 1, loadingService: false}})
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }
  getCloneScopeBy(id:any): Observable<any> {
    const endPoint = `${this.baseURL.getCloneScopes}/${id}`;
    return this.http.get<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  changeStatus(id: any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.changeStatus}/${id}`;
    return this.http.patch<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  // Tax Settings API methods
  getTaxSettings(id: any): Observable<any> {
    const endPoint = `${this.baseURL.taxSettings}/${id}/tax-settings`;
    return this.http.get<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  saveTaxSettings(id: any, model: any): Observable<any> {
    const endPoint = `${this.baseURL.taxSettings}/${id}/tax-settings`;
    return this.http.post<any>(endPoint, model)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  deleteTaxSettings(id: any): Observable<any> {
    const endPoint = `${this.baseURL.taxSettings}/${id}/tax-settings`;
    return this.http.delete<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  getTaxBreakdown(id: any): Observable<any> {
    const endPoint = `${this.baseURL.taxSettings}/${id}/tax-breakdown`;
    return this.http.get<any>(endPoint)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  /**
   * Calculate scope tax in real-time based on form data
   *
   * Stage 6 Phase 6: Real-time tax calculation with 500ms debouncing
   *
   * @param proposalId Proposal ID
   * @param scopeId Scope ID (null for new scopes)
   * @param costBreakdown Cost and profit breakdown from form
   * @returns Observable with tax calculation result
   */
  calculateScopeRealtime(proposalId: number, scopeId: number | null, costBreakdown: any): Observable<any> {
    const endPoint = `${this.baseURL.taxSettings}/${proposalId}/calculate-scope-realtime`;
    const payload = {
      scope_id: scopeId,
      cost_breakdown: costBreakdown
    };
    return this.http.post<any>(endPoint, payload)
      .pipe(tap(data => (this.jsonService.doStringify(data))) , catchError(this.errorHandlerMessage));
  }

  /**
   * Update source address (customer/jobsite) from proposal
   *
   * @param proposalId Proposal ID
   * @param payload Address data with source_type
   * @returns Observable with updated record
   */
  updateSourceAddress(proposalId: number, payload: any): Observable<any> {
    const endPoint = `${this.baseURL.detail}/${proposalId}/update-source-address`;
    return this.http.post<any>(endPoint, payload)
      .pipe(tap(data => (this.jsonService.doStringify(data))), catchError(this.errorHandlerMessage));
  }

  errorHandler(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }

  errorHandlerMessage(error: HttpErrorResponse) {
    return observableThrowError(error.error || 'Server Error');
  }
}
